import type { UI } from '@/v2/types';

import { Box, Flex, Container } from '@qga/roo-ui/components';

import { useCampaign } from '@/v2/contexts/campaign';
import { useDestination } from '@/v2/contexts/destination';

import { Hero } from '@/v2/components/ui/Hero';
import { Layout } from '@/v2/components/layout/Layout';

import { useShowMap } from './hooks/useShowMap';

// Legacy components
import CampaignBanner from '../../../../components/Campaigns/CampaignBanner';
import CheckAvailability from '../../../../components/DestinationPage/components/CheckAvailability';
import DataLayer from '../../../../components/DestinationPage/components/DataLayer';
import Description from '../../../../components/DestinationPage/components/Description';
import { GetPackagesCheapestOfferProvider } from '../../../../components/DestinationPage/hooks/useGetPackagesCheapestOffer';
import ImportantInformation from '../../../../components/ImportantInformation';
import MapView from '../../../../components/DestinationPage/components/MapView';
import PropertyOptions from '../../../../components/DestinationPage/components/PropertyOptions';
import RelatedDestinations from '../../../../components/DestinationPage/components/RelatedDestinations';
import Text from '../../../../components/Text';
import ValueProposition from '../../../../components/ValueProposition';
import usePageViewEvent from '@/v2/hooks/usePageViewEvent';

import FAQs from '../../../../components/DestinationPage/components/FAQs/FAQs';

// Note: Layout called here temporarily with props
// - needs to go to _app when v2 is complete

interface Props {
  importantInformation: Array<UI.ImportantInformation>;
  navigation: Array<UI.Navigation>;
  campaigns: Array<UI.Campaign>;
  informationMessage: UI.Message | null;
  warningMessage: UI.Message | null;
  marketingMessage: UI.MarketingMessage | null;
}

const DestinationView = (props: Props) => {
  const { description, hero, searchConfig, theme, faqs } = useDestination()!;

  const campaign = useCampaign()!;

  // visibility
  const showCampaignBanner = campaign && campaign?.destinationPromoText?.text;
  const showMap = useShowMap();

  const pageName = theme ? 'Theme' : 'Destination';

  usePageViewEvent({ type: pageName.toLowerCase() });

  return (
    <GetPackagesCheapestOfferProvider>
      <Layout {...props}>
        {showMap ? (
          <Box data-testid="DESTINATION_PAGE_MAP_VIEW">
            <DataLayer pageType={`${pageName} Map View`} />
            <MapView />
          </Box>
        ) : (
          <Box data-testid="DESTINATION_PAGE">
            <DataLayer pageType={`${pageName} Page`} />
            <Hero {...hero} />
            <Box
              position="relative"
              top={[null, null, '-32px']}
              mb={['5', null, searchConfig && '0']}
            >
              <Container
                px="3"
                mx={['0', null, 'auto']}
                id="check-availability"
              >
                {searchConfig && <CheckAvailability />}
                {showCampaignBanner && (
                  <Box mx={['4', null, '0']} mt={['4', null, '6']}>
                    <CampaignBanner
                      message={campaign?.destinationPromoText.text}
                    />
                  </Box>
                )}
              </Container>
            </Box>
            <Box>
              <ValueProposition my="6" withTitle />
            </Box>
            <Box>
              <Container id="property-options">
                <PropertyOptions campaigns={props.campaigns} />
              </Container>
              {description && <Description />}
              {faqs?.length ? (
                <Box pb="8" pt="8">
                  <FAQs faqs={faqs} ga4Category={pageName.toLowerCase()} />
                </Box>
              ) : null}
              <RelatedDestinations />
              <Box bg="background.card" pt={[5, null, 10]}>
                <Flex flexDirection="column">
                  <Container>
                    {showCampaignBanner && (
                      <Box mb={6}>
                        <Text hint>
                          {campaign?.destinationPromoText.termsConditions}
                        </Text>
                      </Box>
                    )}
                    <ImportantInformation />
                  </Container>
                </Flex>
              </Box>
            </Box>
          </Box>
        )}
      </Layout>
    </GetPackagesCheapestOfferProvider>
  );
};

export default DestinationView;
